import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../trpc";
import { getQueryKey } from "@trpc/react-query";
import type { AuthenticationsOutput } from "@zapier/bff";

export function useAuthenticationSharing() {
  const queryClient = useQueryClient();

  const authenticationsKey = getQueryKey(
    trpc.authentication.authentications,
    undefined,
    "any",
  );

  const createAuthMembership =
    trpc.authentication.createAuthMembership.useMutation({
      // Callback to handle optimistic updates in RQ cache
      onMutate: ({ customuserId, id }) => {
        if (!id || !customuserId) {
          return;
        }

        // Update Authentication under the authentications list key
        queryClient.cancelQueries({ queryKey: authenticationsKey });
      },
      // Callback to handle cache invalidation after mutation is completed
      onSettled: () => {
        // Invalidate authentications key to refetch data
        queryClient.invalidateQueries({ queryKey: authenticationsKey });
      },
    });

  const deleteAuthMembership =
    trpc.authentication.deleteAuthMembership.useMutation({
      // Callback to handle optimistic updates in RQ cache
      onMutate: ({ customuserId, id }) => {
        if (!id || !customuserId) {
          return;
        }

        // Update Authentication under the authentications list key
        queryClient.cancelQueries({ queryKey: authenticationsKey });
        queryClient.setQueriesData(
          {
            queryKey: authenticationsKey,
            type: "active",
          },
          (old: AuthenticationsOutput | undefined) => {
            if (old?.results) {
              const updatedAuthenticationsArray = old?.results.map((item) =>
                item.id === id
                  ? {
                      ...item,
                      members: item?.members?.filter(
                        (item) => String(item.id) !== String(customuserId),
                      ),
                    }
                  : item,
              );

              // this needs to return a new object ref for RQ to detect the change
              return {
                ...old,
                results: updatedAuthenticationsArray,
              };
            }

            return old;
          },
        );
      },
      // Callback to handle cache invalidation after mutation is completed
      onSettled: () => {
        // Invalidate authentications key to refetch data
        queryClient.invalidateQueries({ queryKey: authenticationsKey });
      },
    });

  const createAuthTeamship = trpc.authentication.createAuthTeamship.useMutation(
    {
      // Callback to handle optimistic updates in RQ cache
      onMutate: ({ teamId, id }) => {
        if (!id || !teamId) {
          return;
        }

        // Update Authentication under the authentications list key
        queryClient.cancelQueries({ queryKey: authenticationsKey });
      },
      // Callback to handle cache invalidation after mutation is completed
      onSettled: () => {
        // Invalidate authentications key to refetch data
        queryClient.invalidateQueries({ queryKey: authenticationsKey });
      },
    },
  );

  const deleteAuthTeamship = trpc.authentication.deleteAuthTeamship.useMutation(
    {
      // Callback to handle optimistic updates in RQ cache
      onMutate: ({ teamId, id }) => {
        if (!id || !teamId) {
          return;
        }

        // Update Authentication under the authentications list key
        queryClient.cancelQueries({ queryKey: authenticationsKey });
        queryClient.setQueriesData(
          {
            queryKey: authenticationsKey,
            type: "active",
          },
          (old: AuthenticationsOutput | undefined) => {
            if (old?.results) {
              const updatedAuthenticationsArray = old?.results.map((item) =>
                item.id === id
                  ? {
                      ...item,
                      groups: item?.groups?.filter(
                        (item) => String(item.id) !== String(teamId),
                      ),
                    }
                  : item,
              );

              // this needs to return a new object ref for RQ to detect the change
              return {
                ...old,
                results: updatedAuthenticationsArray,
              };
            }

            return old;
          },
        );
      },
      // Callback to handle cache invalidation after mutation is completed
      onSettled: () => {
        // Invalidate authentications key to refetch data
        queryClient.invalidateQueries({ queryKey: authenticationsKey });
      },
    },
  );

  const createAuthenticationShares =
    trpc.authentication.createShares.useMutation({
      // Callback to handle optimistic updates in RQ cache
      onMutate: () => {
        // Update Authentication under the authentications list key
        queryClient.cancelQueries({ queryKey: authenticationsKey });
      },
      // Callback to handle cache invalidation after mutation is completed
      onSettled: () => {
        // Invalidate authentications key to refetch data
        queryClient.invalidateQueries({ queryKey: authenticationsKey });
      },
    });

  const deleteAuthenticationShares =
    trpc.authentication.deleteShares.useMutation({
      // Callback to handle optimistic updates in RQ cache
      onMutate: () => {
        // Update Authentication under the authentications list key
        queryClient.cancelQueries({ queryKey: authenticationsKey });
      },
      // Callback to handle cache invalidation after mutation is completed
      onSettled: () => {
        // Invalidate authentications key to refetch data
        queryClient.invalidateQueries({ queryKey: authenticationsKey });
      },
    });

  return {
    createAuthMembership: createAuthMembership.mutateAsync,
    createAuthTeamship: createAuthTeamship.mutateAsync,
    deleteAuthMembership: deleteAuthMembership.mutateAsync,
    deleteAuthTeamship: deleteAuthTeamship.mutateAsync,
    isCreateAuthMembershipLoading: createAuthMembership.isPending,
    isCreateAuthTeamshipLoading: createAuthTeamship.isPending,
    isDeleteAuthMembershipLoading: deleteAuthMembership.isPending,
    isDeleteAuthTeamshipLoading: deleteAuthTeamship.isPending,
    createAuthShares: createAuthenticationShares.mutateAsync,
    isCreateAuthSharesLoading: createAuthenticationShares.isPending,
    deleteAuthShares: deleteAuthenticationShares.mutateAsync,
    isDeleteAuthSharesLoading: deleteAuthenticationShares.isPending,
  };
}
